import { createInsertSchema } from 'drizzle-zod';
import type { Table } from 'drizzle-orm';
import {
  Group,
  Journey,
  LearningPath,
  Product,
  User,
  Level,
  Class,
  Exercise as DbExercise,
  UserXGroup,
  Person,
  EventCategory,
  Event,
  LearningProgress,
  Unit,
  Ability,
  Woman,
  PRODUCT_KINDS,
  PRODUCT_BILLINGS,
  ConversationTopic,
} from './models';
import { z } from 'zod/v4';
import {
  localizedSchema,
  storyCardsSchema,
  storySourceSchema,
  womanJobStatusEnum,
  womanStoryStatusEnum,
} from '../schemas/woman-story';

const createSchema = <T extends Table>(table: T) =>
  createInsertSchema(table).omit({ createdAt: true, updatedAt: true } as any);

const getZodTypeName = (field: any) =>
  field?._def?.typeName ||
  field?.def?.type ||
  field?.constructor?.name ||
  typeof field;

const describeField = <T extends z.ZodTypeAny | undefined>(field: T, labelKey: string, fieldName: string) => {
  if (!field) {
    console.debug('[model.schemas] describeField: missing field', { fieldName, labelKey });
    return field;
  }
  if (typeof field.describe !== 'function') {
    console.debug('[model.schemas] describeField: describe not available', {
      fieldName,
      labelKey,
      fieldType: getZodTypeName(field),
      defKeys: Object.keys(field?._def ?? {}),
    });
    return field;
  }
  const describedField = field.describe(labelKey);
  console.debug('[model.schemas] describeField: applied description', {
    fieldName,
    labelKey,
    originalType: getZodTypeName(field),
  });
  return describedField;
};

const applyFieldDescriptions = (
  shape: Record<string, z.ZodTypeAny>,
  labelMap: Record<string, string>
) => {
  const nextShape: Record<string, z.ZodTypeAny> = { ...shape };

  Object.entries(labelMap).forEach(([fieldName, labelKey]) => {
    if (nextShape[fieldName]) {
      nextShape[fieldName] = describeField(nextShape[fieldName], labelKey, fieldName) as z.ZodTypeAny;
    } else {
      console.debug('[model.schemas] applyFieldDescriptions: field missing in shape', {
        fieldName,
        labelKey,
        availableKeys: Object.keys(nextShape),
      });
    }
  });

  return nextShape;
};

// TODO: Remove type assertion when drizzle-zod fixes .omit() type inference bug
const baseUserSchemaShape = createSchema(User).omit({ userId: true }).shape;

const userFieldLabelMap = {
  firstName: 'students.form.labels.firstName',
  lastName: 'students.form.labels.lastName',
  email: 'students.form.labels.email',
  phone: 'students.form.labels.phone',
  nickname: 'students.form.labels.nickname',
  language: 'students.form.labels.language',
  isAdmin: 'students.form.labels.isAdmin',
  isStudent: 'students.form.labels.isStudent',
  isTeacher: 'students.form.labels.isTeacher',
  levelId: 'students.form.labels.level',
  profileImageUrl: 'students.form.labels.profileImage',
  enabled: 'students.form.labels.enabled',
  status: 'students.form.labels.status',
  type: 'students.form.labels.type',
  gender: 'students.form.labels.gender',
};

const baseUserSchema = z.object({
  ...applyFieldDescriptions(baseUserSchemaShape, userFieldLabelMap),
});
export const userUpdateSchema = baseUserSchema;
export type UserUpdateInput = z.infer<typeof userUpdateSchema>;

// export const userInsertSchema = baseUserSchema;

// export const userUpdateSchema = z.object({
//   ...baseUserSchema.shape,
//   firstName: z.string().min(5, { message: "First name is required" }),
//   lastName: z.string().min(5, { message: "Last name is required" }),
//   email: z.string().refine((val) => val.includes('@'), { message: "Email isn't valid" }),
// });

// TODO: Remove type assertion when drizzle-zod fixes .omit() type inference bug
const baseGroupSchema = createSchema(Group);
const baseGroupShape = baseGroupSchema.shape;
const groupFieldLabelMap = {
  name: 'groups.form.labels.name',
  description: 'groups.form.labels.description',
  startDate: 'groups.form.labels.startDate',
  endDate: 'groups.form.labels.endDate',
  levelId: 'groups.form.labels.level',
  teacherId: 'groups.form.labels.teacher',
  recordingsFolderUrl: 'groups.form.labels.recordingsFolderUrl',
  classNotesUrl: 'groups.form.labels.classNotesUrl',
};
const describedGroupShape = applyFieldDescriptions(baseGroupShape, groupFieldLabelMap);
const usersField = z.array(z.object({
  value: z.string().uuid(),
  label: z.string()
})).describe('groups.form.labels.users');
const requiredUsersField = usersField.min(1, 'Select at least one student.').describe('groups.form.labels.users');

export const groupInsertSchema = z.object({
  ...describedGroupShape,
  users: usersField,
});

export const groupUpdateSchema = z.object({
  ...describedGroupShape,
  users: requiredUsersField,
});
export type GroupUpdateInput = z.infer<typeof groupUpdateSchema>;


export const journeyInsertSchema = createSchema(Journey).omit({ journeyId: true });
export const journeyUpdateSchema = journeyInsertSchema;

export const learningPathInsertSchema = createSchema(LearningPath).omit({ pathId: true });
export const learningPathUpdateSchema = learningPathInsertSchema;
export type LearningPathInsertInput = z.infer<typeof learningPathInsertSchema>;

// drizzle-zod turns the jsonb columns into a generic JSON schema, so the
// poster content fields are re-declared with their real shapes here.
export const productInsertSchema = createSchema(Product).omit({ productId: true }).extend({
  coverImageUrl: z.union([z.string().url(), z.literal('')]).nullable().optional(),
  galleryImageUrls: z.array(z.string().url()).max(4).nullable().optional(),
  tagline: z.string().max(120).nullable().optional(),
  audience: z.string().nullable().optional(),
  outcomes: z.array(z.string().max(160)).max(8).nullable().optional(),
  teacherNote: z.string().nullable().optional(),
  kind: z.enum(PRODUCT_KINDS).default('journey'),
  billing: z.enum(PRODUCT_BILLINGS).default('one_time'),
  trialDays: z.number().int().min(0).max(365).nullable().optional(),
  journeyId: z.string().uuid().nullable().optional(),
}).superRefine((value, ctx) => {
  // A journey product must point at a journey; the base product never does.
  if (value.kind === 'journey' && !value.journeyId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['journeyId'], message: 'A journey product needs a journey' });
  }
  if (value.kind === 'base' && value.journeyId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['journeyId'], message: 'The base product has no single journey' });
  }
});
export const productUpdateSchema = productInsertSchema;
export type ProductInsertInput = z.infer<typeof productInsertSchema>;

export const levelInsertSchema = createSchema(Level).omit({ levelId: true });

export const conversationTopicInsertSchema = createSchema(ConversationTopic).omit({ conversationTopicId: true });
export const conversationTopicUpdateSchema = conversationTopicInsertSchema;
export const levelUpdateSchema = levelInsertSchema;

export const abilityInsertSchema = createSchema(Ability).omit({ abilityId: true });
export const abilityUpdateSchema = abilityInsertSchema;

export const userXGroupInsertSchema = createSchema(UserXGroup);
export const userXGroupUpdateSchema = userXGroupInsertSchema.partial();

const basePersonSchema = createSchema(Person).omit({ personId: true }).extend({
  countryId: z.string().uuid().nullable().optional(),
  stateId: z.string().uuid().nullable().optional(),
  bio: z.string().max(5000, "Bio must be 5000 characters or less").optional(),
  leveling: z.string().max(5000, "Leveling notes must be 5000 characters or less").nullable().optional(),
});
export const personInsertSchema = basePersonSchema;
export const personUpdateSchema = basePersonSchema.partial().merge(
  z.object({
    personId: z.string().uuid().optional()
  })
);
export type PersonUpdateInput = z.infer<typeof personUpdateSchema>;

// TODO: Remove type assertion when drizzle-zod fixes .omit() type inference bug
const baseClassSchema = createSchema(Class) as unknown as z.ZodObject<any>;
export const classInsertSchema = baseClassSchema.omit({ classId: true }) as unknown as z.ZodObject<any>;
export const classUpdateSchema = classInsertSchema.partial().merge(
  z.object({
    classId: baseClassSchema.shape.classId
  })
).strip();
export type ClassUpdateInput = z.infer<typeof classUpdateSchema>;

const baseEventCategorySchema = createSchema(EventCategory).omit({
  eventCategoryId: true,
});

export const eventCategoryInsertSchema = baseEventCategorySchema;

export const eventCategoryUpdateSchema = baseEventCategorySchema.partial().merge(
  z.object({
    eventCategoryId: z.string().uuid(),
  })
);

export type EventCategoryUpdateInput = z.infer<typeof eventCategoryUpdateSchema>;

const baseEventSchema = createSchema(Event).omit({
  eventId: true,
});

export const eventInsertSchema = baseEventSchema;

export const eventUpdateSchema = baseEventSchema.partial().merge(
  z.object({
    eventId: z.string().uuid(),
  })
);

export type EventUpdateInput = z.infer<typeof eventUpdateSchema>;

// Learning Progress schemas
export const learningProgressInsertSchema = createInsertSchema(LearningProgress).omit({ learningProgressId: true });
export const learningProgressUpdateSchema = learningProgressInsertSchema.partial();

// Unit update schema (includes new metadata fields)
export const unitUpdateSchema = createInsertSchema(Unit).omit({ unitId: true }).partial();

// Woman schemas. drizzle-zod turns jsonb / typed-text columns into generic
// schemas, so the story fields are re-declared with their real shapes.
export const womanInsertSchema = createSchema(Woman).omit({ womanId: true }).extend({
  name: z.string().trim().min(1),
  tagline: localizedSchema.nullable().optional(),
  storyCards: storyCardsSchema.optional(),
  storyStatus: womanStoryStatusEnum.optional(),
  sources: z.array(storySourceSchema).nullable().optional(),
  portraitStatus: womanJobStatusEnum.optional(),
  draftStatus: womanJobStatusEnum.optional(),
});
export const womanUpdateSchema = womanInsertSchema.partial();
export type WomanInsertInput = z.infer<typeof womanInsertSchema>;
export type WomanUpdateInput = z.infer<typeof womanUpdateSchema>;
